<template>
  <!-- 💬 Self-contained mini-mockup of the chosen variant.
       Every theme variant defines the SAME CSS custom properties
       (defined in app/assets/css/main.css). This component scopes a
       fresh set of those vars to itself by inlining the variant's
       palette, so the preview renders independent of whatever
       theme is currently live on <html>. That way the cards stay
       readable side-by-side regardless of which one is active. -->
  <div class="preview" :style="paletteStyles">
    <!-- Faux sidebar -->
    <div class="prv-sidebar">
      <div class="prv-logo">
        <div class="prv-logo-dot" />
      </div>
      <div class="prv-nav">
        <div class="prv-nav-item active"><span /></div>
        <div class="prv-nav-item"><span /></div>
        <div class="prv-nav-item"><span /></div>
        <div class="prv-nav-item"><span /></div>
      </div>
    </div>

    <!-- Workspace -->
    <div class="prv-main">
      <div class="prv-header">
        <div class="prv-search" />
        <div class="prv-avatar" />
      </div>
      <div class="prv-kpis">
        <div class="prv-kpi" v-for="i in 3" :key="i">
          <div class="prv-kpi-label" />
          <div class="prv-kpi-val" />
        </div>
      </div>
      <div class="prv-panel">
        <div class="prv-bars">
          <div v-for="(h, i) in barHeights" :key="i" class="prv-bar" :style="{ height: h + '%' }" />
        </div>
      </div>
    </div>

    <!-- Active overlay tick -->
    <div v-if="isActive" class="prv-active-tick">
      <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 8l3.5 3.5L13 5" stroke-linecap="round" stroke-linejoin="round" /></svg>
    </div>
  </div>
</template>

<script setup lang="ts">
// 💬 Each variant's palette is duplicated here in shorthand (we
// only need the surfaces that show up in the mockup — sidebar,
// header chrome, panel bg, accent gradient, app background). This
// is intentionally NOT shared with main.css to keep the preview
// totally decoupled from runtime CSS-var resolution; if main.css
// ever fails to load, the preview still renders.

import type { LightVariant } from '~/composables/useTheme'

const props = defineProps<{ variant: LightVariant, isActive?: boolean }>()

interface Palette {
  appBg: string
  sidebarBg: string
  sidebarText: string
  sidebarTextActive: string
  sidebarBgActive: string
  sidebarLogo: string
  chromeBg: string
  chromeBorder: string
  chromeSearchBg: string
  panelBg: string
  panelBorder: string
  textPrimary: string
  textSecondary: string
  accentFrom: string
  accentTo: string
}

const palettes: Record<LightVariant, Palette> = {
  pearl: {
    appBg: '#FAFAFA', sidebarBg: '#FFFFFF', sidebarText: '#71717A', sidebarTextActive: '#18181B', sidebarBgActive: '#F4F4F5', sidebarLogo: '#6366F1',
    chromeBg: '#FFFFFF', chromeBorder: '#EDEDED', chromeSearchBg: '#F4F4F5',
    panelBg: '#FFFFFF', panelBorder: '#EDEDED', textPrimary: '#18181B', textSecondary: '#71717A',
    accentFrom: '#6366F1', accentTo: '#8B5CF6',
  },
  aurora: {
    appBg: 'linear-gradient(135deg,#FBF5FF 0%,#FFE9DC 50%,#E0F0FF 100%)', sidebarBg: 'rgba(255,255,255,0.55)',
    sidebarText: '#8A7DAE', sidebarTextActive: '#4C1D95', sidebarBgActive: 'rgba(167,139,250,0.18)',
    sidebarLogo: 'linear-gradient(135deg,#A78BFA,#F472B6)',
    chromeBg: 'rgba(255,255,255,0.55)', chromeBorder: 'rgba(255,255,255,0.7)', chromeSearchBg: 'rgba(255,255,255,0.6)',
    panelBg: 'rgba(255,255,255,0.55)', panelBorder: 'rgba(255,255,255,0.7)', textPrimary: '#2D2150', textSecondary: '#8A7DAE',
    accentFrom: '#A78BFA', accentTo: '#F472B6',
  },
  glacier: {
    appBg: 'linear-gradient(180deg,#EAF1F8 0%,#DDE7F2 100%)', sidebarBg: '#1E2A47', sidebarText: '#94A3B8', sidebarTextActive: '#FFFFFF',
    sidebarBgActive: 'rgba(96,165,250,0.18)', sidebarLogo: 'linear-gradient(135deg,#60A5FA,#06B6D4)',
    chromeBg: '#FFFFFF', chromeBorder: '#DDE7F2', chromeSearchBg: '#FFFFFF',
    panelBg: '#FFFFFF', panelBorder: '#DDE7F2', textPrimary: '#0F172A', textSecondary: '#64748B',
    accentFrom: '#3B82F6', accentTo: '#06B6D4',
  },
  twilight: {
    appBg: 'radial-gradient(circle at 90% 0%, rgba(244,114,182,0.18) 0%, transparent 40%), radial-gradient(circle at 0% 100%, rgba(129,140,248,0.20) 0%, transparent 45%), linear-gradient(180deg,#F4F1FB 0%,#E9E6F7 100%)',
    sidebarBg: '#1A1530', sidebarText: '#94A3B8', sidebarTextActive: '#FFFFFF', sidebarBgActive: 'rgba(167,139,250,0.20)',
    sidebarLogo: 'linear-gradient(135deg,#A78BFA,#F0ABFC)',
    chromeBg: 'rgba(255,255,255,0.6)', chromeBorder: 'rgba(255,255,255,0.7)', chromeSearchBg: 'rgba(255,255,255,0.65)',
    panelBg: 'rgba(255,255,255,0.55)', panelBorder: 'rgba(255,255,255,0.7)', textPrimary: '#2D1B5E', textSecondary: '#5B5474',
    accentFrom: '#8B5CF6', accentTo: '#EC4899',
  },
  sage: {
    appBg: '#FAF8F2', sidebarBg: '#2D3A2E', sidebarText: '#A4B0A4', sidebarTextActive: '#F2F4EF', sidebarBgActive: 'rgba(132,169,140,0.18)',
    sidebarLogo: '#84A98C',
    chromeBg: '#FFFFFF', chromeBorder: '#E8E2D0', chromeSearchBg: '#FAF8F2',
    panelBg: '#FFFFFF', panelBorder: '#E8E2D0', textPrimary: '#2A2823', textSecondary: '#5C5648',
    accentFrom: '#52796F', accentTo: '#84A98C',
  },
  latte: {
    appBg: 'linear-gradient(135deg,#FFF8E7 0%,#FFEEDD 100%)', sidebarBg: 'rgba(255,255,255,0.7)', sidebarText: '#A0825F',
    sidebarTextActive: '#92400E', sidebarBgActive: 'rgba(245,158,11,0.14)',
    sidebarLogo: 'linear-gradient(135deg,#F59E0B,#EC4899)',
    chromeBg: 'rgba(255,255,255,0.7)', chromeBorder: 'rgba(255,255,255,0.85)', chromeSearchBg: 'rgba(255,255,255,0.65)',
    panelBg: 'rgba(255,255,255,0.7)', panelBorder: 'rgba(255,255,255,0.85)', textPrimary: '#3D2914', textSecondary: '#8B6F4E',
    accentFrom: '#F59E0B', accentTo: '#EC4899',
  },
}

const palette = computed(() => palettes[props.variant])

const paletteStyles = computed(() => ({
  '--p-app': palette.value.appBg,
  '--p-sidebar': palette.value.sidebarBg,
  '--p-sidebar-text': palette.value.sidebarText,
  '--p-sidebar-text-active': palette.value.sidebarTextActive,
  '--p-sidebar-bg-active': palette.value.sidebarBgActive,
  '--p-sidebar-logo': palette.value.sidebarLogo,
  '--p-chrome': palette.value.chromeBg,
  '--p-chrome-border': palette.value.chromeBorder,
  '--p-search': palette.value.chromeSearchBg,
  '--p-panel': palette.value.panelBg,
  '--p-panel-border': palette.value.panelBorder,
  '--p-text': palette.value.textPrimary,
  '--p-text-2': palette.value.textSecondary,
  '--p-accent-from': palette.value.accentFrom,
  '--p-accent-to': palette.value.accentTo,
}))

const barHeights = [55, 70, 40, 85, 60, 75, 50, 90, 65, 45]
</script>

<style scoped>
.preview {
  position: relative;
  height: 180px;
  display: grid;
  grid-template-columns: 56px 1fr;
  background: var(--p-app);
  overflow: hidden;
}

.prv-sidebar {
  background: var(--p-sidebar);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  display: flex;
  flex-direction: column;
  padding: 10px 8px;
  gap: 12px;
}
.prv-logo { display: flex; justify-content: center; }
.prv-logo-dot {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  background: var(--p-sidebar-logo);
}
.prv-nav { display: flex; flex-direction: column; gap: 4px; }
.prv-nav-item {
  height: 18px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 6px;
}
.prv-nav-item.active { background: var(--p-sidebar-bg-active); }
.prv-nav-item span {
  display: block;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: var(--p-sidebar-text);
  opacity: 0.6;
}
.prv-nav-item.active span { background: var(--p-sidebar-text-active); opacity: 1; }

.prv-main {
  padding: 10px 10px 0 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.prv-header {
  height: 26px;
  background: var(--p-chrome);
  border: 1px solid var(--p-chrome-border);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.prv-search {
  width: 70px;
  height: 12px;
  border-radius: 3px;
  background: var(--p-search);
  border: 1px solid var(--p-chrome-border);
}
.prv-avatar {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--p-accent-from), var(--p-accent-to));
}

.prv-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.prv-kpi {
  background: var(--p-panel);
  border: 1px solid var(--p-panel-border);
  border-radius: 6px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.prv-kpi-label {
  height: 4px;
  width: 60%;
  border-radius: 2px;
  background: var(--p-text-2);
  opacity: 0.5;
}
.prv-kpi-val {
  height: 8px;
  width: 80%;
  border-radius: 2px;
  background: var(--p-text);
}

.prv-panel {
  flex: 1;
  background: var(--p-panel);
  border: 1px solid var(--p-panel-border);
  border-radius: 6px;
  padding: 8px;
  display: flex;
  align-items: end;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.prv-bars { display: flex; gap: 3px; align-items: end; flex: 1; height: 100%; }
.prv-bar {
  flex: 1;
  border-radius: 2px 2px 0 0;
  background: linear-gradient(180deg, var(--p-accent-from), var(--p-accent-to));
}

.prv-active-tick {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--p-accent-from);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18);
}
</style>
